// ─────────────────────────────────────────────────────────────────────────────
// Design Tokens — sourced from Figma Foundations
// https://figma.com/design/ut6qHOEjPOfUVKH9zLQH7y/...?node-id=509-8761
//
// Structure:
//   palette   — raw primitive color scales (Slate / Cobalt / Blue / Green / Purple / Red)
//   color     — semantic tokens (Background / Text / Primary / Status / Divider)
//   typography — all text styles from Figma (spread-ready sx objects)
//   spacing   — base-4 scale (Dimension/space/N → N×4px)
//   radius    — border radius
//   shadow    — elevation
//
// Usage (컴포넌트에서 원시값 대신 반드시 토큰을 사용하세요):
//
//   import { color, typography, spacing, radius, shadow } from 'theme';
//
//   색상:      sx={{ color: color.text.secondary }}         // '#32373f' 대신
//   타이포:    sx={{ ...typography.small.base }}            // fontSize/fontWeight/lineHeight 한번에
//   간격:      sx={{ gap: spacing[3] }}                     // 12px 대신
//   라운드:    sx={{ borderRadius: radius.lg }}             // '12px' 대신
//   팔레트 직접 참조는 지양하고, color.* semantic 토큰을 우선 사용하세요.
// ─────────────────────────────────────────────────────────────────────────────


// ─── Primitive Palette ────────────────────────────────────────────────────────
// Raw color scales. Reference via palette.slate[900] etc.
// Prefer semantic tokens (color.*) in components.

export const palette = {
  // Neutral — pure gray scale (no color tint)
  neutral: {
    0:   '#010102',
    25:  '#08090a',
    50:  '#0f1011',
    100: '#141516',
    125: '#191a1c',
    150: '#1e1f21',
    200: '#23252a',
    250: '#28282c',
    300: '#34343a',
    400: '#3e3e44',
    500: '#62666d',
    600: '#8a8f98',
    700: '#b9bcc1',
    750: '#d9d9d9',
    800: '#e6e6e6',
    850: '#f3f4f5',
    875: '#f5f6f7',
    900: '#f7f8f8',
    950: '#ffffff',
  },

  // Slate — blue-tinted neutral scale used for backgrounds, text, borders (light mode)
  slate: {
    0:   '#010204',
    25:  '#080a0f',
    50:  '#0e1018',
    100: '#13161e',
    125: '#171b23',
    150: '#191d26',
    200: '#22262f',
    250: '#272c36',
    300: '#32373f',
    400: '#454e5b',
    500: '#5c667a',
    600: '#727b8c',
    700: '#a6acb6',
    750: '#cbd1dc',
    800: '#e2e5ec',
    850: '#eff1f5',
    875: '#f2f4f7',
    900: '#f5f7fa',
    950: '#ffffff',
  },

  // Cobalt — primary brand (nav accent, CTA)
  cobalt: {
    0:   '#edf2fd',
    100: '#cfdafa',
    200: '#a5bcf6',
    300: '#7b9cf1',
    400: '#5c7eec',
    500: '#4364e8',
    600: '#3355cc',
    700: '#2244a8',
    800: '#153383',
    900: '#0a2260',
  },

  // Blue — informational / "Waiting" status
  blue: {
    0:   '#eaf4ff',
    100: '#d4e8ff',
    200: '#a9ceff',
    300: '#72aeff',
    400: '#4294ff',
    500: '#1b84ff',
    600: '#1168d4',
    700: '#0a4fa8',
    800: '#05367c',
    900: '#021e50',
  },

  // Purple — "Delay" status
  purple: {
    0:   '#f3f0ff',
    100: '#e2daff',
    200: '#c5b5ff',
    300: '#a68eff',
    400: '#8a68ff',
    500: '#7047f5',
    600: '#5835cc',
    700: '#4226a3',
    800: '#2c187a',
    900: '#180c52',
  },

  // Green — Success status
  green: {
    0:   '#edfaf4',
    100: '#c8f0e0',
    200: '#91e0c2',
    300: '#4dcba0',
    400: '#1fbb86',
    500: '#10a870',
    600: '#0c8a5c',
    700: '#086c48',
    800: '#054e34',
    900: '#023020',
  },

  // Coral — pink-red (입금 취소 / 위험 상태 등에 사용)
  coral: {
    0:   '#fff0f2',
    100: '#ffd4dc',
    200: '#ffa8b8',
    300: '#ff7292',
    400: '#f5476e',
    500: '#e8224f',
    600: '#c01840',
    700: '#980f31',
    800: '#700824',
    900: '#480417',
  },

  // Red — pure red scale
  red: {
    0:   '#fff2f3',
    100: '#ffe0e1',
    200: '#ffb8bb',
    300: '#ff8287',
    400: '#f55560',
    500: '#e5484d',
    600: '#be3238',
    700: '#952028',
    800: '#3d0a0c',
    900: '#200406',
  },
} as const;


// ─── Semantic Color Tokens (Light mode) ───────────────────────────────────────
// Intent-based tokens. Use these in components instead of raw palette values.

export const color = {
  // Background surfaces
  bg: {
    default:  palette.slate[950],  // #ffffff — page background
    paper:    palette.slate[900],  // #f5f7fa — card / panel surface
    subtle:   palette.slate[875],  // #f2f4f7 — subtle section bg
    elevated: palette.slate[800],  // #e2e5ec — elevated / hover surface
  },

  // Text
  text: {
    primary:    palette.slate[125],  // #171b23 — main body text (was 32373f in old theme → using Figma value)
    secondary:  palette.slate[300],  // #32373f — secondary label
    hint:       palette.slate[400],  // #454e5b — placeholder / description
    disabled:   palette.slate[600],  // #727b8c — disabled state
    onPrimary:  palette.slate[950],  // #ffffff — text on filled primary button
  },

  // Primary brand (Cobalt)
  primary: {
    main:         palette.cobalt[500],  // #4364e8
    hover:        palette.cobalt[400],  // #5c7eec
    disabled:     palette.cobalt[300],  // #7b9cf1
    dark:         palette.cobalt[600],  // #3355cc
    contrastText: palette.slate[950],   // #ffffff
  },

  // Success (Green)
  success: {
    main:         palette.green[400],   // #1fbb86
    softBg:       palette.green[0],     // #edfaf4
    softColor:    palette.green[500],   // #10a870
    contrastText: palette.slate[950],   // #ffffff
  },

  // Waiting — informational blue
  waiting: {
    main:         palette.blue[500],    // #1b84ff
    softBg:       palette.blue[0],      // #eaf4ff
    softColor:    palette.blue[500],    // #1b84ff
    contrastText: palette.slate[950],   // #ffffff
  },

  // Delay — purple
  delay: {
    main:         palette.purple[500],  // #7047f5
    softBg:       palette.purple[0],    // #f3f0ff
    softColor:    palette.purple[500],  // #7047f5
    contrastText: palette.slate[950],   // #ffffff
  },

  // Divider / Border
  divider: {
    faint:  palette.slate[875],  // #f2f4f7 — barely visible separator
    subtle: palette.slate[800],  // #e2e5ec — standard border
    strong: palette.slate[750],  // #cbd1dc — prominent border
  },
} as const;


// ─── Typography ───────────────────────────────────────────────────────────────
// All text styles from Figma Typography page (node 509-8769).
// Spread directly into MUI sx: sx={{ ...typography.display.xl }}

const PRETENDARD = "'Pretendard', 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif";
const MONO       = "'JetBrains Mono', monospace";

export const typography = {
  fontFamily:     PRETENDARD,
  monoFontFamily: MONO,

  // Display — hero / dashboard KPI numbers
  display: {
    xl:   { fontFamily: PRETENDARD, fontWeight: 500, fontSize: 72, lineHeight: '72px', letterSpacing: '-1.584px' },
    lg:   { fontFamily: PRETENDARD, fontWeight: 500, fontSize: 64, lineHeight: '64px', letterSpacing: '-1.408px' },
    base: { fontFamily: PRETENDARD, fontWeight: 500, fontSize: 48, lineHeight: '48px', letterSpacing: '-1.056px' },
  },

  // Heading — section titles
  heading: {
    1:    { fontFamily: PRETENDARD, fontWeight: 600, fontSize: 32, lineHeight: '36px', letterSpacing: '-0.704px' },
    2:    { fontFamily: PRETENDARD, fontWeight: 600, fontSize: 24, lineHeight: '28px', letterSpacing: '-0.288px' },
    3:    { fontFamily: PRETENDARD, fontWeight: 600, fontSize: 20, lineHeight: '28px', letterSpacing: '-0.24px'  },
  },

  // Body — main content text
  body: {
    lg:       { fontFamily: PRETENDARD, fontWeight: 500, fontSize: 18, lineHeight: '24px', letterSpacing: '-0.17px'  },
    emphasis:  { fontFamily: PRETENDARD, fontWeight: 600, fontSize: 17, lineHeight: '24px', letterSpacing: '-0.45px'  },
    base:     { fontFamily: PRETENDARD, fontWeight: 400, fontSize: 16, lineHeight: '22px', letterSpacing: '-0.70px'  },
    md:       { fontFamily: PRETENDARD, fontWeight: 500, fontSize: 16, lineHeight: '22px', letterSpacing: '-0.70px'  },
    semibold: { fontFamily: PRETENDARD, fontWeight: 600, fontSize: 16, lineHeight: '22px', letterSpacing: '-0.70px'  },
  },

  // Small — UI labels, table cells, form fields
  small: {
    base:     { fontFamily: PRETENDARD, fontWeight: 400, fontSize: 14, lineHeight: '20px', letterSpacing: '-0.17px'  },
    md:       { fontFamily: PRETENDARD, fontWeight: 500, fontSize: 14, lineHeight: '20px', letterSpacing: '-0.17px'  },
    semibold: { fontFamily: PRETENDARD, fontWeight: 600, fontSize: 14, lineHeight: '20px', letterSpacing: '-0.17px'  },
  },

  // Caption — table headers, tags, meta info
  caption: {
    lg:       { fontFamily: PRETENDARD, fontWeight: 500, fontSize: 14, lineHeight: '20px', letterSpacing: '-0.24px'  },
    base:     { fontFamily: PRETENDARD, fontWeight: 400, fontSize: 13, lineHeight: '16px', letterSpacing: '-0.24px'  },
    md:       { fontFamily: PRETENDARD, fontWeight: 500, fontSize: 13, lineHeight: '16px', letterSpacing: '-0.24px'  },
    semibold: { fontFamily: PRETENDARD, fontWeight: 600, fontSize: 13, lineHeight: '16px', letterSpacing: '-0.24px'  },
  },

  // Label — badges, chips, icon buttons
  label: {
    base:     { fontFamily: PRETENDARD, fontWeight: 400, fontSize: 12, lineHeight: '16px', letterSpacing: '-0.24px'  },
    md:       { fontFamily: PRETENDARD, fontWeight: 500, fontSize: 12, lineHeight: '16px', letterSpacing: '-0.24px'  },
    semibold: { fontFamily: PRETENDARD, fontWeight: 600, fontSize: 12, lineHeight: '16px', letterSpacing: '-0.24px'  },
  },

  // Micro — smallest text, footnotes
  micro: {
    base: { fontFamily: PRETENDARD, fontWeight: 400, fontSize: 11, lineHeight: '16px' },
    md:   { fontFamily: PRETENDARD, fontWeight: 500, fontSize: 11, lineHeight: '16px' },
  },

  // Link — anchor / hyperlink text styles
  link: {
    lg:      { fontFamily: PRETENDARD, fontWeight: 400, fontSize: 16, lineHeight: '24px' },
    md:      { fontFamily: PRETENDARD, fontWeight: 500, fontSize: 15, lineHeight: '36px' },
    sm:      { fontFamily: PRETENDARD, fontWeight: 500, fontSize: 14, lineHeight: '20px' },
    caption: { fontFamily: PRETENDARD, fontWeight: 400, fontSize: 13, lineHeight: '20px', letterSpacing: '-0.13px' },
  },

  // Mono — code, IDs, technical strings (JetBrains Mono)
  mono: {
    body:    { fontFamily: MONO, fontWeight: 400, fontSize: 14, lineHeight: '20px' },
    caption: { fontFamily: MONO, fontWeight: 400, fontSize: 13, lineHeight: '20px' },
    label:   { fontFamily: MONO, fontWeight: 400, fontSize: 12, lineHeight: '16px' },
  },
} as const;


// ─── Spacing ──────────────────────────────────────────────────────────────────
// Base-4 scale. Token name = multiplier (space[N] = N×4px).
// Usage: gap: spacing[3] → 12px

export const spacing = {
  1:  4,
  2:  8,
  3:  12,
  4:  16,
  5:  20,
  6:  24,
  7:  28,
  8:  32,
  10: 40,
  12: 48,
  16: 64,
  20: 80,
  24: 96,
  32: 128,
} as const;


// ─── Border Radius ────────────────────────────────────────────────────────────

export const radius = {
  none: 0,    // no rounding
  sm:   2,    // tight — ID badge, small tag
  md:   4,    // button, input, small elements
  base: 6,    // default — button, form field
  lg:   8,    // card inner sections
  xl:   12,   // card
  xxl:  16,   // page panel, sidebar
  full: 9999, // pill / chip
} as const;


// ─── Shadow / Elevation ───────────────────────────────────────────────────────
// Figma: Foundations > Elevation (node 509-8775)

export const shadow = {
  subtle:   '0px 1.5px 0px rgba(0,0,0,0.03)',                                                              // Elevation/Subtle   — drop, y 1.5, blur 0,  3% alpha
  inset:    'inset 0px 0px 12px rgba(0,0,0,0.20)',                                                         // Elevation/Inset    — inner, blur 12,       20% alpha
  ring:     '0px 0px 0px 1px rgba(0,0,0,0.20)',                                                            // Elevation/Ring     — drop, spread 1,       20% alpha
  elevated: '0px 2px 4px rgba(0,0,0,0.15)',                                                                // Elevation/Elevated — drop, y 2, blur 4,    15% alpha
  dialog:   '0px 0px 1px rgba(0,0,0,0.08), 0px 1px 1px rgba(0,0,0,0.07), 0px 5px 2px rgba(0,0,0,0.01)', // Elevation/Dialog   — 3-layer drop
  focus:    '0px 0px 6px rgba(0,0,0,0.10)',                                                                // Elevation/Focus    — drop, blur 6,         10% alpha
} as const;
